/**
 * Pest inspections: the daily quick look (start and end of every day) and the weekly detailed walk-through, both with
 * the same two outcomes: All clear, or Found something (what, where), which opens a pest Report pre-filled.
 *
 * Source (library.ts "pest-prevention"; Source protocols: pest control inspection methodology; monitoring):
 * "Start and end of every day: quick walk-through of the kitchen, storage and serving area." "Weekly: a detailed
 * checklist inspection." Traps and bait stations are serviced by the licensed operator (tasks.ts "pest-service");
 * staff look at them and report, never move or bait them. Inspectors look for pest inspections documented under
 * fc-pest-control (Food Code 6-501.111, 6-501.112).
 *
 * Both are Records of one template ("pest_check", templates.ts), so the inspection history is one list (binder, Prove).
 */
import { shortName } from "./personname.ts";
import { SOMEWHERE_ELSE, somewhereElse } from "./incidents.ts";
import type { CellValue, Incident, IsoDate, IsoDateTime, LogEntry } from "./model.ts";
import { counts } from "./corrections.ts";

export { PEST_TEMPLATE_ID, PEST_QUICK_LOOK_LABEL, PEST_WALKTHROUGH_LABEL } from "./cadence.ts";
import { PEST_TEMPLATE_ID } from "./cadence.ts";
export type PestKind = "quick look" | "walk-through";
export type PestOutcome = "all clear" | "found something";

export const PEST_CITATIONS = ["Food Code 6-501.111", "Food Code 6-501.112", "Source protocols: pest control inspection methodology; monitoring"];

/** The six things the weekly walk-through looks for in each area (tasks.ts "pest-walkthrough" how, library "pest-prevention"). */
export const PEST_LOOK_FOR: readonly { key: string; label: string }[] = [
  { key: "droppings", label: "Droppings" },
  { key: "gnaw", label: "Gnaw marks" },
  { key: "insects", label: "Live or dead insects" },
  { key: "gaps", label: "Gaps or holes" },
  { key: "water", label: "Standing water" },
  { key: "traps", label: "Traps and bait stations checked" },
];

/** What a finding can be (one tap), from the same six, plus the way out. */
export const PEST_FOUND_CHOICES: readonly string[] = ["Droppings", "Gnaw marks", "Insects", "Gap or hole", "Standing water", "Something in a trap", "Something else"];

/** The source's own areas, for a place that hasn't named its areas: "kitchen, storage and serving area". */
export const PEST_DEFAULT_AREAS: readonly string[] = ["Kitchen", "Storage", "Serving area"];

/** The areas to walk: the location's own, or the source's three when it has none. */
export function pestAreas(areas: readonly string[] | undefined): string[] {
  return areas && areas.length ? [...areas] : [...PEST_DEFAULT_AREAS];
}

export const PEST_NO_SIGNS = "No signs";

export interface PestCheckInput {
  kind: PestKind;
  outcome: PestOutcome;
  /** Areas looked at. Empty means the location's areas (or the source's three). */
  areas?: string[];
  /** The things checked for. Empty means all six (a quick look checks for the same signs, just faster). */
  looked?: string[];
  /** Found something: what was seen. */
  seen?: string;
  /** Found something: where. */
  area?: string;
  initials?: string;
  date: IsoDate;
  /** "HH:MM" local. */
  time: string;
}

/** The Record's values for a pest check (template "pest_check"). All clear writes "No signs" as what was seen. */
export function pestCheckValues(i: PestCheckInput, locationAreas?: readonly string[]): Record<string, CellValue> {
  const areas = i.areas?.length ? i.areas : pestAreas(locationAreas);
  const looked = i.looked?.length ? i.looked : PEST_LOOK_FOR.map((x) => x.label);
  const found = i.outcome === "found something";
  return {
    date: i.date, time: i.time, kind: i.kind, areas: areas.join(", "), looked: looked.join(", "),
    outcome: i.outcome, seen: found ? (i.seen?.trim() || null) : PEST_NO_SIGNS, area: found ? (i.area?.trim() || null) : null,
    initials: i.initials?.trim() || null,
  };
}

/** Why a pest check can't be saved (found something needs what and where), or undefined when it can. */
export function pestCheckProblem(values: Record<string, CellValue | undefined>): string | undefined {
  const outcome = String(values.outcome ?? "").toLowerCase();
  if (outcome !== "all clear" && outcome !== "found something") return 'Pick "All clear" or "Found something".';
  if (outcome === "found something") {
    if (!String(values.seen ?? "").trim()) return "Say what you found.";
    if (!String(values.area ?? "").trim()) return "Say where you found it.";
  }
  return undefined;
}

/** The report's details line: "Found on the weekly pest walk-through: droppings." */
export function pestReportDetails(seen: string, kind: PestKind): string {
  const what = seen.trim().replace(/\.$/, "");
  return `Found on the ${kind === "walk-through" ? "weekly pest walk-through" : "quick pest look"}: ${what.charAt(0).toLowerCase()}${what.slice(1)}.`;
}

/** A place report's area: one of the location's areas, or "Somewhere else: …" (incidents.ts reportArea). */
export function pestReportArea(area: string, locationAreas: readonly string[] | undefined): string {
  const a = area.trim();
  if (!locationAreas?.length || locationAreas.includes(a) || a.startsWith(`${SOMEWHERE_ELSE}:`)) return a;
  return somewhereElse(a);
}

/**
 * The Report screen, pre-filled: /report?type=pest&area=…&details=…&from=<entry id>. The same path works on the web
 * (react-router) and the phone (expo-router). `from` names the pest check it came from.
 */
export function pestReportLink(p: { seen: string; area: string; kind: PestKind; entryId?: string; locationAreas?: readonly string[] }): string {
  const q = new URLSearchParams({ type: "pest", area: pestReportArea(p.area, p.locationAreas), details: pestReportDetails(p.seen, p.kind) });
  if (p.entryId) q.set("from", p.entryId);
  return `/report?${q.toString()}`;
}

type EntryLike = Pick<LogEntry, "id" | "locationId" | "templateId" | "date" | "values" | "createdAt" | "createdBy">;
type IncidentLike = Pick<Incident, "id" | "locationId" | "type" | "area" | "reportedAt" | "status"> & { fromRecordId?: string };

export interface PestHistoryRow {
  entryId: string;
  date: IsoDate;
  at: IsoDateTime;
  kind: PestKind;
  outcome: PestOutcome;
  /** Areas looked at, as written. */
  areas: string;
  seen?: string;
  area?: string;
  initials?: string;
  by: string;
  /** "All clear: no signs" or "Found: droppings, Storage". */
  line: string;
  /** Found something: the pest report it led to, when one was sent (same area, within a day). */
  reportId?: string;
  reportStatus?: Incident["status"];
  /** Found something without a report yet: where to send it. */
  reportLink?: string;
}

const DAY_MS = 86_400_000;
const sameArea = (a: string, b: string) => a.replace(/^Somewhere else:\s*/i, "").trim().toLowerCase() === b.replace(/^Somewhere else:\s*/i, "").trim().toLowerCase();

/** "Found: droppings, Storage" / "All clear: no signs". */
export function pestLine(r: { outcome: string; seen?: string | null; area?: string | null }): string {
  if (r.outcome !== "found something") return "All clear: no signs";
  const what = (r.seen ?? "").trim();
  return `Found: ${what ? what.charAt(0).toLowerCase() + what.slice(1) : "something"}${r.area ? `, ${r.area}` : ""}`;
}

/**
 * The inspection history, newest first. Each finding is linked to the pest report sent from it: the first pest report
 * at the same location and area, sent at or after the check and within a day, not already linked to another finding.
 */
export function pestHistory(entries: EntryLike[], incidents: IncidentLike[], opts: { names?: Record<string, string>; locationAreas?: readonly string[] } = {}): PestHistoryRow[] {
  const checks = entries.filter((e) => counts(e) && e.templateId === PEST_TEMPLATE_ID).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const pests = incidents.filter((i) => i.type === "pest").sort((a, b) => a.reportedAt.localeCompare(b.reportedAt));
  const used = new Set<string>();
  const rows = checks.map((e): PestHistoryRow => {
    const v = e.values;
    const outcome: PestOutcome = v.outcome === "found something" ? "found something" : "all clear";
    const kind: PestKind = v.kind === "walk-through" ? "walk-through" : "quick look";
    const seen = v.seen ? String(v.seen) : undefined;
    const area = v.area ? String(v.area) : undefined;
    const row: PestHistoryRow = {
      entryId: e.id, date: e.date, at: e.createdAt, kind, outcome, areas: String(v.areas ?? ""), by: shortName(opts.names?.[e.createdBy]), line: pestLine({ outcome, seen, area }),
      ...(seen ? { seen } : {}), ...(area ? { area } : {}), ...(v.initials ? { initials: String(v.initials) } : {}),
    };
    if (outcome === "found something") {
      const until = Date.parse(e.createdAt) + DAY_MS;
      // Exact first: the report sent from this check (fromRecordId); else the matched one, for older reports.
      const inc = pests.find((i) => !used.has(i.id) && i.fromRecordId === e.id)
        ?? pests.find((i) => !used.has(i.id) && !i.fromRecordId && i.locationId === e.locationId && i.reportedAt >= e.createdAt && Date.parse(i.reportedAt) <= until && (!area || sameArea(i.area, area)));
      if (inc) { used.add(inc.id); row.reportId = inc.id; row.reportStatus = inc.status; }
      else row.reportLink = pestReportLink({ seen: seen ?? "something", area: area ?? "", kind, entryId: e.id, locationAreas: opts.locationAreas });
    }
    return row;
  });
  return rows.reverse();
}

/** The last day a weekly walk-through was recorded (schedule.ts "pest-walkthrough" is due 7 days after it). */
export function lastPestWalkthrough(entries: Pick<LogEntry, "templateId" | "date" | "values">[]): IsoDate | undefined {
  let last: IsoDate | undefined;
  for (const e of entries) if (counts(e) && e.templateId === PEST_TEMPLATE_ID && e.values.kind === "walk-through" && (!last || e.date > last)) last = e.date;
  return last;
}

/**
 * The checklist task seeded before the walk-through became its own check ("Pest walk-through", task_done). Existing
 * locations still have it; schedule.ts skips it so the walk-through doesn't show up twice.
 */
export function isLegacyPestWalkthrough(t: { title: string; category: string }): boolean {
  return t.category === "pest" && t.title.trim().toLowerCase() === "pest walk-through";
}

/** GET /orgs/:orgId/pest-checks?locationId=&from=&to= (the web and phone clients' pestChecks). */
export interface PestChecksResponse {
  rows: PestHistoryRow[];
  /** The last weekly walk-through on file (any date), when there is one. */
  lastWalkthrough?: IsoDate;
}
